// Constants
import { CUSTOM_FIELD_COLUMN_PREFIX } from "@ms/uno-constants/lib/local/TaskGridConstants";
// Models
import { compareOrderHints } from "@ms/uno-utilities/lib/local/Comparators";
import { Temporal } from "temporal-polyfill-lite";
import { CustomFieldChoice } from "../client/planner/customFields/CustomFieldChoice";
import { CustomFieldDefinition } from "../client/planner/customFields/CustomFieldDefinition";
import { CustomFieldRollup, CustomFieldValueType } from "../service/planner/enumerations/IPlannerEnumerations";
/**
 * Creates a temporary client-side custom field definition for use in the "add field" flow.
 * The returned object has no server state — it carries a caller-supplied temp id and
 * placeholder values for fields that are only populated after server creation (archivalInfo,
 * createdBy, etc.).
 *
 * @param fieldId     Caller-generated temporary id (e.g. from LocalIdGenerator.generate())
 * @param planId      Id of the plan the field will belong to
 */ export function createTempCustomFieldDefinition(fieldId, planId) {
    const now = Temporal.Now.instant();
    const defaultIdentitySet = {
        user: {
            id: "",
            tenantId: ""
        },
        application: {
            id: "",
            tenantId: ""
        }
    };
    return new CustomFieldDefinition({
        id: fieldId,
        version: "1",
        planId,
        displayName: "",
        valueType: CustomFieldValueType.Text,
        rollupType: CustomFieldRollup.NoRollup,
        choiceOptions: [],
        archivalInfo: {
            isArchived: false,
            justification: null,
            modifiedBy: null,
            modifiedDate: null
        },
        createdBy: defaultIdentitySet,
        createdDateTime: now,
        lastModifiedBy: defaultIdentitySet,
        lastModifiedDateTime: now,
        deletedBy: null,
        deletedDateTime: null
    });
}
/**
 * Creates a temporary client-side custom field choice for use in the "add/edit field" flow.
 * The returned object has no server state — it carries a caller-supplied temp id and
 * extensions until the parent field is persisted.
 *
 * @param choiceId      Caller-generated temporary id (e.g. from LocalIdGenerator.generate())
 * @param displayName   Initial display name for the choice
 * @param orderHint     Order hint that determines display position among sibling choices
 */ export function createTempCustomFieldChoice(choiceId, displayName, orderHint) {
    return new CustomFieldChoice({
        id: choiceId,
        version: "1",
        displayName,
        extensions: {
            orderHint
        }
    });
}
/**
 * Returns a sorted copy of the choices array ordered ascending by orderHint.
 * This is the canonical display order: smaller orderHint → earlier in the list.
 */ export function getSortedCustomFieldChoices(choices) {
    return [
        ...choices
    ].sort((a, b)=>compareOrderHints(a.extensions.orderHint, b.extensions.orderHint));
}
/**
 * Returns true if the given value type supports grouping in the board view.
 * Currently only SingleChoiceText and Date fields are groupable.
 */ export function isGroupableCustomFieldDefinition(valueType) {
    return valueType === CustomFieldValueType.SingleChoiceText || valueType === CustomFieldValueType.Date;
}
/**
 * Returns a new {@link ICustomFieldDefinition} that is identical to {@code definition} except
 * with {@code newValueType} as the valueType.  Also resets dependent fields:
 *
 * - rollup → NoRollup when switching away from Number
 * - choiceOptions → empty when switching away from SingleChoiceText
 *
 * Use this helper when the user changes the field type in the add/edit popover because
 * `valueType` is intentionally absent from {@link MutableCustomFieldDefinitionProperties}
 * (the server does not support mutating it on an existing persisted field).
 */ export function cloneCustomFieldDefinitionWithValueType(definition, newValueType) {
    return new CustomFieldDefinition({
        id: definition.id,
        version: definition.version,
        planId: definition.planId,
        displayName: definition.displayName,
        valueType: newValueType,
        rollupType: newValueType === CustomFieldValueType.Number ? definition.rollupType : CustomFieldRollup.NoRollup,
        choiceOptions: newValueType === CustomFieldValueType.SingleChoiceText ? definition.choiceOptions : [],
        archivalInfo: definition.archivalInfo,
        createdBy: definition.createdBy,
        createdDateTime: definition.createdDateTime,
        lastModifiedBy: definition.lastModifiedBy,
        lastModifiedDateTime: definition.lastModifiedDateTime,
        deletedBy: definition.deletedBy,
        deletedDateTime: definition.deletedDateTime
    });
}
/**
 * Convert a custom field definition ID to a column ID
 * @param definitionId The custom field definition ID
 * @returns The prefixed column ID (e.g. "cf_<definitionId>")
 */ export function toCustomFieldColumnId(definitionId) {
    return `${CUSTOM_FIELD_COLUMN_PREFIX}${definitionId}`;
}
/**
 * Check if a column ID represents a custom field column
 * @param columnId The column ID to check
 * @returns True if the column ID has the custom field prefix
 */ export function isCustomFieldColumn(columnId) {
    return columnId.startsWith(CUSTOM_FIELD_COLUMN_PREFIX);
}
/**
 * Extract the custom field definition ID from a custom field column ID
 * @param columnId The prefixed custom field column ID
 * @returns The raw custom field definition ID with the prefix stripped
 */ export function getCustomFieldDefinitionId(columnId) {
    return columnId.slice(CUSTOM_FIELD_COLUMN_PREFIX.length);
}
